import React, { useState, useRef, useEffect } from 'react';

/**
 * ColorPicker Component
 *
 * Reusable color picker with hex input and preset colors
 *
 * @param {string} label - Input label
 * @param {string} value - Current color (hex format)
 * @param {function} onChange - Callback when color changes
 * @param {boolean} showAlpha - Whether to show alpha/opacity slider
 * @param {string[]} presets - Array of preset color hex values
 */
function ColorPicker({
  label,
  value,
  onChange,
  showAlpha = false,
  presets = [
    '#ef4444', // Red
    '#f59e0b', // Orange
    '#10b981', // Green
    '#06b6d4', // Cyan
    '#3b82f6', // Blue
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#6b7280', // Gray
  ]
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [localColor, setLocalColor] = useState(value);
  const [alpha, setAlpha] = useState(1);
  const pickerRef = useRef(null);

  // Parse alpha from rgba if present
  useEffect(() => {
    if (value.startsWith('rgba')) {
      const match = value.match(/rgba?\([\d\s,]+,\s*([\d.]+)\)/);
      if (match) {
        setAlpha(parseFloat(match[1]));
      }
    }
    setLocalColor(value);
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  const handleColorChange = (newColor) => {
    setLocalColor(newColor);
    if (showAlpha && alpha < 1) {
      // Convert hex to rgba
      const rgb = hexToRgb(newColor);
      if (rgb) {
        onChange(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`);
      }
    } else {
      onChange(newColor);
    }
  };

  const handleAlphaChange = (newAlpha) => {
    setAlpha(newAlpha);
    const rgb = hexToRgb(localColor);
    if (rgb) {
      onChange(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${newAlpha})`);
    }
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const getDisplayColor = () => {
    if (value.startsWith('rgba') || value.startsWith('rgb')) {
      return value;
    }
    return value;
  };

  return (
    <div className="space-y-1" ref={pickerRef}>
      <label className="block text-sm font-medium text-text">
        {label}
      </label>

      {/* Color Preview Button */}
      <button
        type="button"
        onClick={() => setShowPicker(!showPicker)}
        className="w-full flex items-center gap-3 px-3 py-2 bg-background border border-muted rounded hover:border-primary transition-colors"
      >
        <div
          className="w-8 h-8 rounded border border-gray-600"
          style={{ backgroundColor: getDisplayColor() }}
        />
        <span className="flex-1 text-left text-text text-sm font-mono">
          {value}
        </span>
        <span className="text-gray-400 text-xs">▼</span>
      </button>

      {/* Color Picker Dropdown */}
      {showPicker && (
        <div className="absolute z-50 mt-1 p-4 bg-gray-800 border border-gray-700 rounded-lg shadow-xl space-y-4">
          {/* Preset Colors */}
          <div>
            <p className="text-xs text-gray-400 mb-2">Preset Colors</p>
            <div className="grid grid-cols-4 gap-2">
              {presets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => handleColorChange(preset)}
                  className={`w-10 h-10 rounded border-2 hover:scale-110 transition-transform ${
                    localColor === preset ? 'border-white' : 'border-gray-600'
                  }`}
                  style={{ backgroundColor: preset }}
                  title={preset}
                />
              ))}
            </div>
          </div>

          {/* Hex Input */}
          <div>
            <p className="text-xs text-gray-400 mb-2">Hex Color</p>
            <input
              type="text"
              value={localColor}
              onChange={(e) => {
                const newColor = e.target.value;
                if (/^#[0-9A-Fa-f]{0,6}$/.test(newColor)) {
                  handleColorChange(newColor);
                }
              }}
              placeholder="#000000"
              className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white text-sm font-mono focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Alpha Slider */}
          {showAlpha && (
            <div>
              <p className="text-xs text-gray-400 mb-2">
                Opacity: {Math.round(alpha * 100)}%
              </p>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={alpha}
                onChange={(e) => handleAlphaChange(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          )}

          {/* Native Color Picker (Fallback) */}
          <div>
            <p className="text-xs text-gray-400 mb-2">Color Picker</p>
            <input
              type="color"
              value={localColor}
              onChange={(e) => handleColorChange(e.target.value)}
              className="w-full h-10 cursor-pointer"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default ColorPicker;
